// Yoga Mells - Village Location Page
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Car } from "lucide-react";
import Navigation from "../../components/shared/Navigation";
import Footer from "../../components/shared/Footer";
import PageBreadcrumb from "../../components/shared/PageBreadcrumb";
import { RetreatPromoBanner } from "../../components/shared/RetreatPromo";
import { usePageMeta, generateLocationSchema, generateFAQSchema, SchemaScript } from "../../components/shared/SEOHelper";

const YogaMells = () => {
  usePageMeta({
    title: "Yoga Classes Mells | Annelie Lily",
    description: "Yoga classes for Mells, Somerset. Small group yoga in Frome, just 5 minutes from Mells village. Hatha, Yin & private sessions. Beginners welcome.",
    canonical: "https://annelielily.com/areas/yoga-mells"
  });

  const locationSchema = generateLocationSchema({
    townName: "Mells",
    description: "Yoga classes for Mells village at Annelie Lily Yoga in Frome. Just 5 minutes away.",
    url: "https://annelielily.com/yoga-mells"
  });

  const faqs = [
    { question: "How far is Frome from Mells?", answer: "Just 5 minutes by car. Mells is one of the closest villages to my studio — you could practically walk it on a nice day." },
    { question: "What yoga classes are available?", answer: "I offer Hatha yoga, Yin yoga, Aerial yoga, and private one-to-one sessions. All held in my Frome studio." },
    { question: "Are beginners welcome?", answer: "Absolutely. Many of my students started as complete beginners. I offer modifications for every pose." },
  ];

  const faqSchema = generateFAQSchema(faqs);

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={locationSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Yoga in Mells" currentSlug="areas/yoga-mells" />
      
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">Yoga for Mells</span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">Yoga Classes Mells</h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-6">
              Living in beautiful Mells village? My yoga studio in Frome is just 5 minutes away — 
              close enough for a regular practice without the drive to Bath or Wells.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><MapPin className="w-4 h-4 text-gold" />Based in Frome</span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700"><Car className="w-4 h-4 text-gold" />5 min from Mells</span>
            </div>
            <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
          </motion.div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">Yoga for Mells Residents</h2>
          <div className="prose-content space-y-4 text-neutral-700">
            <p>
              Mells is one of Somerset's most beautiful villages, and it's practically on my doorstep. 
              If you live in Mells and want to start yoga, you couldn't be better placed — my studio 
              is just a 5-minute drive into Frome.
            </p>
            <p>
              My <Link to="/yoga-classes" className="text-gold hover:underline">yoga classes</Link> are 
              small (6-8 people), so you get personal attention rather than being lost in a crowd. 
              Whether you want the active practice of <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> or 
              the deep release of <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link>, there's a class for you.
            </p>
            <p>
              <Link to="/yoga-classes/beginner-yoga" className="text-gold hover:underline">New to yoga?</Link> Many of my 
              students started as complete beginners. I offer modifications for every pose, so you 
              work at whatever level suits your body.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-10 text-center">Yoga Classes for Mells</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Hatha Yoga", desc: "Traditional practice for strength, flexibility, and calm.", link: "/yoga-classes/hatha-yoga" },
              { title: "Yin Yoga", desc: "Slow, deep stretches for release and relaxation.", link: "/yoga-classes/yin-yoga" },
              { title: "Private Sessions", desc: "One-to-one yoga tailored to your needs.", link: "/yoga-classes/private-yoga" },
            ].map((service) => (
              <Link key={service.title} to={service.link} className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group">
                <h3 className="font-heading text-xl text-neutral-800 mb-2 group-hover:text-gold">{service.title}</h3>
                <p className="text-neutral-600 text-sm mb-3">{service.desc}</p>
                <span className="text-gold text-sm font-medium">Learn more →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">Questions from Mells Residents</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                <p className="text-neutral-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">Ready to Try a Class?</h2>
          <p className="text-neutral-600 mb-6">Get in touch and I'll help you find the right class.</p>
          <Link to="/contact" className="btn-primary">Book a Class <ArrowRight className="w-4 h-4 inline ml-2" /></Link>
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-neutral-600 text-sm mb-4">Nearby areas:</p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { name: "Nunney", link: "/areas/yoga-nunney" },
              { name: "Beckington", link: "/areas/yoga-beckington" },
              { name: "Radstock", link: "/areas/yoga-radstock" },
              { name: "Shepton Mallet", link: "/areas/yoga-shepton-mallet" },
              { name: "Somerset", link: "/areas/yoga-somerset" },
            ].map((area) => (
              <Link key={area.name} to={area.link} className="text-sm text-gold hover:underline">{area.name}</Link>
            ))}
          </div>
        </div>
      </section>

      {/* Retreat Promotion */}
      <RetreatPromoBanner variant="highlight" />

      <Footer />
    </div>
  );
};

export default YogaMells;
